import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { scaledCoordinate } from '../drawing.helper'
import { primaryDrawingColor } from '../../../../assets/colors'

export type HorizontalLineDrawingType = 'horizontalLine' | 'horizontalRay'

export type HorizontalLineDrawingState = {
    p1?: Point
}

export type HorizontalLineToolOptions = {
    drawingType: HorizontalLineDrawingType
}

export type HorizontalLineDrawingSettings = {
    lineType: LineTypes,
    lineColor: ColorValue,
    lineWidth: number,
    point1: Point
}

const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

class HorizontalLineTool extends DrawingBase<HorizontalLineToolOptions, HorizontalLineDrawingSettings, HorizontalLineDrawingState> {
    scaledCoordinates = {
        p1x: 0,
        p1y: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        let start = this.getStartCoordinate()
        let end = this.getEndCoordinate()
        if (!start || !end || start.y === 0) return

        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.moveTo(start.x, start.y)
        ctx.lineTo(end.x, end.y)
        ctx.stroke()
        ctx.closePath()
    };

    calculateLineAttributes() {
        const { p1 } = this._state
        if (!p1) return
        let p1x = this.timeToCoordinate(p1.x)
        let p1y = this.priceToCoordinate(p1.y)
        if (p1x && p1y) {
            p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates = { p1x, p1y }
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        const lineWidth = this._settings.lineWidth * window.devicePixelRatio
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max((lineWidth) * 3, 8), 3]
        } else if (this._settings.lineType === 'dotted') {
            lineDash = [lineWidth, lineWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    getStartCoordinate(): PointCoordinates | void {
        const { p1x, p1y } = this.scaledCoordinates
        return {
            x: this.options.drawingType === 'horizontalLine' ? 0 : p1x,
            y: p1y
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1 } = this._state
        if (!p1) return []
        const min = Math.min(p1.y, p1.y)
        const max = Math.max(p1.y, p1.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    getEndCoordinate(): PointCoordinates | void {
        let { p1y } = this.scaledCoordinates
        return { x: this.canvasWidth, y: p1y }
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { p1 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price,
            coordinates: {
                x: this.getTimeScaleWidth() / 2,
                y: this.priceToCoordinate(price) ?? 0
            }
        }
        if (!p1) {
            this._state.p1 = point
        }
        p1 && this.onDrawingEnd()
        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1 } = this._state
        if (p1) {
            handles.push({
                ...p1,
                coordinates: {
                    x: this.getTimeScaleWidth() / 2,
                    y: this.priceToCoordinate(p1?.y ?? 0) ?? 0
                }
            })
        }
        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const lineStart = this.getStartCoordinate()
        const lineEnd = this.getEndCoordinate()
        if (!lineStart || !lineEnd) {
            return null
        }
        const lineY = lineStart.y
        const yPoint = lineY ?? 0
        if (scaledY < yPoint - THRESHOLD / 3 || scaledY > yPoint + THRESHOLD / 3) {
            return null
        }

        const startX = Math.min(lineStart.x, lineEnd.x)
        if (scaledX < startX) {
            return null
        }
        const endX = Math.max(lineStart.x, lineEnd.x)
        if (scaledX > endX) {
            return null
        }
        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1 } = this._state
        if (!p1) return
        const p1x = p1.x
        switch (id) {
            case 'p1':
                p1.x = this.options.drawingType === 'horizontalLine' ? p1x : time
                p1.y = price
                break
        }
    }

    onSettingsUpdate = (settings: Partial<HorizontalLineDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<HorizontalLineDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: this.options.drawingType === 'horizontalLine' ? '#1 (price)' : '#1 (price, bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: this.options.drawingType !== 'horizontalLine',
                        showY: true
                    }
                }
            ]
        }]
    }

    getSettingsValues(): HorizontalLineDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        return settings
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'horizontalLine':
                return 'horizontalLine'
            case 'horizontalRay':
                return 'horizontalRay'
        }
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof HorizontalLineDrawingSettings)[]
    }
}

export default HorizontalLineTool